import type {
    LocationAsRelativeRaw,
    LocationQuery,
    LocationQueryRaw,
    RouteLocationNormalized,
    Router,
    RouteRecordRaw,
    RouterHistory,
} from 'vue-router'
import { createRouter as vueCreateRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { difference, entries, filter, invert, values } from 'remeda'
import type { ParseOptions, StringifyOptions } from 'query-string'
import qs from 'query-string'
import Region from '@/views/Region.vue'
import { getNumber as getNumberOrUndefined, includes } from '@/helpers'
import {
    CatalogueSortOrder,
    EngagementRingDesignFeatureLabelCode,
    EngagementRingDesignStyleLabelCode,
    EngagementRingStyleLabelCode,
    GemsApi_StoneCategory,
    GemsApi_StoneColour,
    GemsApi_StoneCrystal,
    GemsApi_StoneShape,
    JewelleryBraceletProductStyle,
    JewelleryDesignStyle,
    JewelleryEarringsProductStyle,
    JewelleryGemstoneCode,
    JewelleryNecklaceProductStyle,
    JewelleryRingProductStyle,
    MetalCode,
    StoneClarityCode,
    StoneColorCode,
    StoneCrystalCode,
    StoneGradeCode,
    StoneShapeCode,
    WeddingRingCategoryLabelCode,
    WeddingRingStyleLabelCode,
} from '@/graphql/api'
import type { ExtractComponentProps } from '@/types/vue'
import { defaultRegion, RegionSlugs } from '@/plugins/region'
import { getFragmentFromHash } from '@/helpers/fragment'
import { lowercase, uppercase } from '@/helpers/string-case'
import { joinStringArrayWith } from '@/helpers/joinStringArrayWith'
import { isValidEmail } from '@/types/email'
import type { BookingStep } from '@/views/Booking.vue'
import type { MagicLinkRouteParams } from './routes'
import type { JewelleriesProps } from '@/views/product/jewellery/Jewelleries.vue'
import type { GetKeyByValue, ReplaceChar } from '@/types/utils'
import type { JewelleriesRedirectProducTypeProps } from '@/views/product/jewellery/JewelleriesRedirectProductType.vue'
import { useScrollPositionResolverStore } from '@/stores/app/lazyLoader'
import { testRoutes } from '@/router/test.ts'
import { getEngagementRingProps } from '@/views/product/engagement-ring/er.ts'
import type { ComputedRef } from 'vue'

export const ROUTE_HOME = 'home'
export const ROUTE_PAGE = 'page'
export const ROUTE_QUESTIONNAIRE = 'questionnaire'

// Blog
export const ROUTE_BLOG = 'blog'
export const ROUTE_BLOG_POST = 'blog-post'
export const ROUTE_BLOG_CATEGORY = 'blog-category'
export const ROUTE_BLOG_TAG = 'blog-tag'
export const ROUTE_BLOG_AUTHOR = 'blog-author'

// ER
export const ROUTE_ENGAGEMENT_RINGS = 'engagement-rings'
export const ROUTE_ENGAGEMENT_RINGS_HOME = 'engagement-rings-home'
export const ROUTE_ENGAGEMENT_RING = 'engagement-ring'
export const ROUTE_ENGAGEMENT_RING_LAB_GROWN = 'engagement-ring-lab-grown'
export const ROUTE_ENGAGEMENT_RING_GEMSTONE = 'engagement-ring-gemstone'
export const ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS =
    'engagement-ring.stone-recommendations'

// WR
export const ROUTE_WEDDING_RINGS = 'wedding-rings'
export const ROUTE_WEDDING_RINGS_HOME = 'wedding-rings-home'
export const ROUTE_WEDDING_RING = 'wedding-ring'
export const ROUTE_READY_TO_GO_RINGS = 'ready-to-go-rings'
export const ROUTE_READY_TO_GO_RING = 'ready-to-go-ring'

// Jewellery
export const ROUTE_JEWELLERIES = 'jewelleries'
export const ROUTE_JEWELLERY = 'jewellery'

// Stone search
export const ROUTE_NATURAL_DIAMOND_SEARCH = 'stone.search.diamond'
export const ROUTE_LAB_GROWN_DIAMOND_SEARCH = 'stone.search.lab_grown_diamond'
export const ROUTE_NATURAL_FANCY_DIAMOND_SEARCH =
    'stone.search.fancy_diamond.natural'
export const ROUTE_LAB_GROWN_FANCY_DIAMOND_SEARCH =
    'stone.search.fancy_diamond.lab_grown'
export const ROUTE_GEMSTONE_SEARCH = 'stone.search.gemstone'
export const ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH =
    'stone.search.non_sku.lab_grown_diamond'
export const ROUTE_DTL_REPORT = 'stone.dtl_report'

// Sign in, impersonation
export const ROUTE_SIGN_IN = 'sign-in'
export const ROUTE_IMPERSONATE = 'impersonate'
export const ROUTE_MAGIC_LINK_REQUEST = 'magic-link-request'
export const ROUTE_MAGIC_LINK = 'magic-link'
export const ROUTE_FORGOTTEN_PASSWORD = 'forgotten-password'

// Cart, checkout
export const ROUTE_CART = 'cart'
export const ROUTE_CHECKOUT = '__checkout_root'
export const ROUTE_CHECKOUT_DELIVERY = 'checkout-delivery'
export const ROUTE_CHECKOUT_PAYMENT = 'checkout-payment'
export const ROUTE_CHECKOUT_PAYMENT_CHECKOUTCOM_RETURN =
    'checkout-payment-checkotcom-return'
export const ROUTE_CHECKOUT_PAYMENT_AFFIRM_RETURN =
    'checkout-payment-affirm-return'
export const ROUTE_CHECKOUT_REVIEW = 'checkout-review'
export const ROUTE_CHECKOUT_COMPLETE = 'checkout-complete'

// Profile
export const ROUTE_PROFILE_DESIGN_STUDIO = 'profile.design-studio'
export const ROUTE_PROFILE_PRODUCTS = 'profile.products'
export const ROUTE_PROFILE_PRODUCT = 'profile.product'
export const ROUTE_PROFILE_MY_ORDERS = 'profile.my-orders'
export const ROUTE_PROFILE_MAKE_PAYMENT = 'profile.make-payment'
export const ROUTE_BOOKING = 'booking'

const engagementRingDesignFeatures = Object.values(
    EngagementRingDesignFeatureLabelCode
)
const catalogueSortOrder = Object.values(CatalogueSortOrder)

// TODO: WHEN NORMALIZING STONE PROPS: REMOVE ALL .filter( here

function getFromHash<T extends string>(
    values: T[],
    value: string | string[] | undefined,
    mapper?: (value: string | undefined) => T | undefined
): T[] {
    if (!value) {
        return []
    }

    if (Array.isArray(value)) {
        return value
            .map(
                mapper ||
                    ((item) =>
                        typeof item === 'string' ? item.toUpperCase() : '')
            )
            .filter((item): item is T => includes(values, item))
    }

    if (typeof value !== 'string') {
        return []
    }

    value = mapper ? mapper(value) : value.toUpperCase()

    if (includes(values, value)) {
        return [value]
    }

    return []
}

function getNumber(value: unknown, onInvalid?: number): number | undefined {
    const num = Number(value)

    if (!isNaN(num)) {
        return num
    }

    return onInvalid
}

function getString(value: unknown, onInvalid?: string): string | undefined {
    return typeof value === 'string' ? value : onInvalid
}

export const homeRoute: LocationAsRelativeRaw = {
    name: ROUTE_HOME,
}

// todo move out of here
export const parseOptions: ParseOptions = {
    arrayFormat: 'bracket',
    parseBooleans: true,
    parseNumbers: true,
    sort: false,
}

export const stringifyOptions: StringifyOptions = {
    arrayFormat: 'bracket',
    sort: false,
    skipEmptyString: true,
    skipNull: true,
}

export const ALL: unique symbol = Symbol()
export type ALL = typeof ALL

function createMapper<const TMap extends Record<string, string>>(
    map: TMap
): {
    toSlug: <K extends keyof TMap>(param: K) => TMap[K]
    fromSlug: <TSlug extends TMap[keyof TMap]>(
        slug: TSlug | string | undefined
    ) => GetKeyByValue<TMap, TSlug>
} {
    const inverseMap = invert(map)

    type InverseKey = keyof typeof inverseMap

    return {
        toSlug: (v) => map[v],
        fromSlug: <TSlug extends TMap[keyof TMap]>(
            s: TSlug | string | undefined
        ) =>
            inverseMap[s as InverseKey] as unknown as GetKeyByValue<
                TMap,
                TSlug
            >,
    }
}

type RouteParamGenerator<
    TParamType extends string,
    TParamName extends string,
    TOptional extends boolean,
    TSlug extends string = string,
> = TOptional extends true
    ? (value: undefined) =>
          | {
                [key in TParamName]?: undefined
            }
          | ((value: TParamType) => {
                [key in TParamName]?: TSlug
            })
    : (value: TParamType) => {
          [key in TParamName]: TSlug
      }

type RouteParamGeneratorAll<TParamType extends string> = (
    value: TParamType | ALL | undefined
) => {
    [key in TParamType]: string | undefined
}

type ParamRegex<
    TParamName extends string,
    TOptional extends boolean,
> = `:${TParamName}(${string})${TOptional extends true ? '?' : ''}`

export type RoutePart<TParamName extends string, TParamType extends string> = {
    name: TParamName
    regex: ParamRegex<TParamName, false>
    extractor: (value: RouteLocationNormalized) => TParamType
    routeParamGenerator: RouteParamGenerator<TParamType, TParamName, false>
}

export type RoutePartWithAll<
    TParamName extends string,
    TParamType extends string,
    TAll extends string,
> = {
    name: TParamName
    regex: ParamRegex<TParamName, true>
    isAll: (value: string | string[] | undefined) => value is TAll
    extractor: (
        value: Pick<RouteLocationNormalized, 'params'>
    ) => TParamType | undefined
    routeParamGenerator: RouteParamGeneratorAll<TParamType>
}

function createRoutePart<
    TParamName extends string,
    TParamType extends string,
    TAll extends string,
>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    all: TAll,
    toSlug: (s: TParamType) => string,
    fromSlug: (slug: string) => TParamType
): RoutePartWithAll<TParamName, TParamType, TAll>

function createRoutePart<TParamName extends string, TParamType extends string>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    toSlug: (s: TParamType) => string,
    fromSlug: (slug: string) => TParamType
): RoutePart<TParamName, TParamType>

function createRoutePart<
    TParamName extends string,
    TParamType extends string,
    TAll extends string,
    const TSlug extends string,
    const TSlugMap extends Record<TParamType, TSlug>,
>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    all: TAll,
    toSlug: <K extends keyof TSlugMap>(param: K) => TSlugMap[K],
    fromSlug: (slug: TSlug) => GetKeyByValue<TSlugMap, TSlug>
): RoutePartWithAll<TParamName, TParamType, TAll>

function createRoutePart<
    TParamName extends string,
    TParamType extends string,
    const TSlugMap extends Record<TParamType, string>,
>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    all: undefined,
    toSlug: <K extends keyof TSlugMap>(param: K) => TSlugMap[K],
    fromSlug: (slug: string) => TParamType
): RoutePart<TParamName, TParamType>

function createRoutePart<
    TParamName extends string,
    TParamType extends string,
    TAll extends string,
>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    all: TAll,
    toSlug?: (slug: string) => Lowercase<string>,
    fromSlug?: (slug: string) => Uppercase<string>
): RoutePartWithAll<TParamName, TParamType, TAll>

function createRoutePart<TParamName extends string, TParamType extends string>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    all: undefined,
    toSlug?: (slug: string) => Lowercase<string>,
    fromSlug?: (slug: string) => Uppercase<string>
): RoutePart<TParamName, TParamType>

function createRoutePart<
    TParamName extends string,
    TParamType extends string,
    TAll extends string,
    const TSlugMap extends Record<TParamType, string>,
    TSlug extends TSlugMap[keyof TSlugMap],
>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    all: TAll | undefined,
    toSlug:
        | ((param: TParamType) => string)
        | (<K extends keyof TSlugMap>(param: K) => TSlugMap[K]) = lowercase,
    fromSlug:
        | ((slug: string) => Uppercase<string>)
        | ((slug: TSlug) => GetKeyByValue<TSlugMap, TSlug>) = uppercase
):
    | RoutePartWithAll<TParamName, TParamType, TAll>
    | RoutePart<TParamName, TParamType> {
    const valuesArray =
        typeof values === 'object' ? Object.values(values) : values

    const slugs = valuesArray.map(toSlug)

    const joiner = joinStringArrayWith('|')

    const regex = `:${name}(${joiner(...slugs)}${all ? `|${all}` : ''})${
        all === undefined ? '' : '?'
    }`

    if (typeof all === 'undefined') {
        const routeParamGenerator: RouteParamGenerator<
            TParamType,
            TParamName,
            false
        > = (value: TParamType) => {
            // if (!value) {
            //     return { [name]: undefined } as {
            //         [key in TParamName]: undefined
            //     }
            // }

            return { [name]: toSlug(value) } as {
                [key in TParamName]: TSlug
            }
        }

        return {
            name,
            regex,
            routeParamGenerator,
            extractor: (route) => {
                const value = route.params[name] as TSlug

                if (typeof value !== 'string') {
                    return undefined
                }

                const mapped = fromSlug(value)

                return includes(valuesArray, mapped) ? mapped : undefined
            },
        } as RoutePart<TParamName, TParamType>
    }

    const isAll = (value: string | string[] | undefined): value is TAll =>
        all === value

    return {
        name,
        regex,
        isAll,
        routeParamGenerator: (value) => {
            if (!value) {
                return { [name]: undefined }
            }

            if (value === ALL) {
                return { [name]: all }
            }

            return { [name]: toSlug(value) }
        },
        extractor: (route) => {
            const value = route.params[name] as TSlug

            if (typeof value !== 'string' || isAll(value)) {
                return undefined
            }

            const mapped = fromSlug(value)

            return includes(valuesArray, mapped) ? mapped : undefined
        },
    } as RoutePartWithAll<TParamName, TParamType, TAll>
}

type CompoundSlug<
    TParamSlug extends string = string,
    THashSlug extends string = string,
> = {
    param: TParamSlug
    hash: THashSlug | undefined
}

function createCompoundMapper<const TMap extends Record<string, CompoundSlug>>(
    map: TMap
): {
    toSlug: <K extends keyof TMap>(param: K) => TMap[K] | undefined
    fromSlug: <TSlug extends TMap[keyof TMap]>(
        slug: TSlug | CompoundSlug | undefined
    ) => GetKeyByValue<TMap, TSlug>[] | undefined
} {
    const inverseMapTwo = entries(map).reduce(
        (acc, [paramType, slug]) => {
            return {
                ...acc,
                [slug.param]: {
                    ...acc[slug.param],
                    [`${slug.hash}`]: paramType,
                },
            }
        },
        {} as Record<string, Record<string, string>>
    )

    const fromSlug = <TSlug extends TMap[keyof TMap]>(
        slug: TSlug | CompoundSlug | undefined
    ) => {
        if (!slug) {
            return undefined
        }

        const param = inverseMapTwo[slug.param]

        if (!param) {
            return undefined
        }

        if (slug.hash) {
            return [param[slug.hash]] as GetKeyByValue<TMap, TSlug>[]
        }

        return values(param) as GetKeyByValue<TMap, TSlug>[] | undefined
    }

    return {
        toSlug: (v) => map[v],
        fromSlug,
    }
}

type CompoundParamName = {
    param: string
    hash: string
}

type CompoundRouteParamGenerator<
    TParamType extends string,
    TParamName extends CompoundParamName,
    TSlug extends CompoundSlug,
> = (value: TParamType | undefined) =>
    | {
          param: { [key in TParamName['param']]: TSlug['param'] }
          hash: { [key in TParamName['hash']]: TSlug['hash'] }
      }
    | {
          param: { [key in TParamName['param']]: undefined }
          hash: { [key in TParamName['hash']]: undefined }
      }

type CompoundRouteParamGeneratorAll<
    TParamType extends string,
    TParamName extends CompoundParamName,
    TSlug extends CompoundSlug,
> = (value: TParamType | ALL | undefined) =>
    | {
          param: {
              [key in TParamName['param']]: TSlug['param']
          }
          hash: {
              [key in TParamName['hash']]: TSlug['hash']
          }
      }
    | {
          param: { [key in TParamName['param']]: undefined }
          hash: { [key in TParamName['hash']]: undefined }
      }

export type CompoundRoutePart<
    TParamName extends CompoundParamName,
    TParamType extends string,
    TSlug extends CompoundSlug,
> = {
    name: TParamName
    regex: ParamRegex<TParamName['param'], false>
    extractor: (value: RouteLocationNormalized) => TParamType[]
    routeParamGenerator: CompoundRouteParamGenerator<
        TParamType,
        TParamName,
        TSlug
    >
}

export type CompoundRoutePartWithAll<
    TParamName extends CompoundParamName,
    TParamType extends string,
    TSlug extends CompoundSlug,
    TAll extends string,
> = {
    name: TParamName
    regex: ParamRegex<TParamName['param'], true>
    isAll: (value: string | string[] | undefined) => value is TAll
    extractor: (
        value: Pick<RouteLocationNormalized, 'params' | 'hash'>
    ) => TParamType[] | undefined
    routeParamGenerator: CompoundRouteParamGeneratorAll<
        TParamType,
        TParamName,
        TSlug
    >
}

function createCompoundRoutePart<
    const TParamName extends CompoundParamName,
    TParamType extends string,
    TAll extends string,
    const TSlug extends CompoundSlug,
    const TSlugMap extends Record<TParamType, TSlug>,
>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    all: TAll,
    toSlug: <K extends keyof TSlugMap>(param: K) => TSlugMap[K] | undefined,
    fromSlug: (
        slug: TSlug | undefined
    ) => GetKeyByValue<TSlugMap, TSlug>[] | undefined
): CompoundRoutePartWithAll<TParamName, TParamType, TSlug, TAll>

// function createCompoundRoutePart<
//     const TParamName extends CompoundParamName,
//     TParamType extends string,
//     const TSlug extends CompoundSlug,
//     const TSlugMap extends Record<TParamType, TSlug>,
// >(
//     name: TParamName,
//     optional: TOptional,
//     values: Record<string, TParamType> | Readonly<TParamType[]>,
//     all: undefined,
//     toSlug: <K extends keyof TSlugMap>(param: K) => TSlugMap[K],
//     fromSlug: (slug: TSlug) => GetKeyByValue<TSlugMap, TSlug>[]
// ): CompoundRoutePart<TParamName, TParamType, TSlug>

function createCompoundRoutePart<
    const TParamName extends CompoundParamName,
    TParamType extends string,
    TAll extends string,
    const TSlugMap extends Record<string, CompoundSlug>,
    const TSlug extends TSlugMap[keyof TSlugMap],
>(
    name: TParamName,
    values: Record<string, TParamType> | Readonly<TParamType[]>,
    all: TAll,
    toSlug: <K extends keyof TSlugMap>(param: K) => TSlugMap[K] | undefined,
    fromSlug: (slug: TSlug) => GetKeyByValue<TSlugMap, TSlug>[] | undefined
):
    | CompoundRoutePartWithAll<TParamName, TParamType, TSlug, TAll>
    | CompoundRoutePart<TParamName, TParamType, TSlug> {
    const valuesArray =
        typeof values === 'object' ? Object.values(values) : values

    const slugs = filter(
        valuesArray.map((slug) => toSlug(slug)?.param),
        Boolean
    ) as TSlug['param'][]

    const joiner = joinStringArrayWith('|')

    const regex = `:${name.param}(${joiner(...slugs)}${all ? `|${all}` : ''})${
        all === undefined ? '' : '?'
    }`

    if (typeof all === 'undefined') {
        const routeParamGenerator: CompoundRouteParamGenerator<
            TParamType,
            TParamName,
            TSlug
        > = (value) => {
            if (!value) {
                return {
                    hash: { [name.hash]: undefined },
                    param: { [name.param]: undefined },
                } as {
                    param: { [key in TParamName['param']]: undefined }
                    hash: { [key in TParamName['hash']]: undefined }
                }
            }

            const slug = toSlug(value)

            if (!slug) {
                return {
                    hash: { [name.hash]: undefined },
                    param: { [name.param]: undefined },
                } as {
                    param: { [key in TParamName['param']]: undefined }
                    hash: { [key in TParamName['hash']]: undefined }
                }
            }

            return {
                hash: { [name.hash]: slug.hash },
                param: { [name.param]: slug.param },
            } as {
                param: { [key in TParamName['param']]: TSlug['param'] }
                hash: { [key in TParamName['hash']]: TSlug['hash'] }
            }
        }

        return {
            name,
            regex,
            routeParamGenerator,
            extractor: (route) => {
                const param = route.params[name.param] as TSlug['param']

                if (typeof param !== 'string') {
                    return { param: undefined, hash: undefined }
                }

                const hash = getFragmentFromHash(route.hash)[
                    name.hash
                ] as TSlug['hash']

                const mapped = fromSlug({
                    param,
                    hash,
                } as TSlug)

                return includes(valuesArray, mapped) ? [mapped] : undefined
            },
        } as CompoundRoutePart<TParamName, TParamType, TSlug>
    }

    const isAll = (value: string | string[] | undefined): value is TAll =>
        all === value

    return {
        name,
        regex,
        isAll,
        routeParamGenerator: (value) => {
            if (!value) {
                return {
                    hash: { [name.hash]: undefined },
                    param: { [name.param]: undefined },
                }
            }

            if (value === ALL) {
                return {
                    hash: { [name.hash]: undefined },
                    param: { [name.param]: all },
                }
            }

            const slug = toSlug(value)

            return {
                hash: { [name.hash]: slug?.hash },
                param: { [name.param]: slug?.param },
            }
        },
        extractor: (route) => {
            const param = route.params[name.param] as TSlug['param']

            if (typeof param !== 'string' || isAll(param)) {
                return undefined
            }

            const hash = getFragmentFromHash(route.hash)[
                name.hash
            ] as TSlug['hash']

            const mapped = fromSlug({
                param,
                hash,
            } as TSlug)

            return mapped
        },
    } as CompoundRoutePartWithAll<TParamName, TParamType, TSlug, TAll>
}

export const shapeListPart = createRoutePart(
    'shape',
    GemsApi_StoneShape,
    'all-shapes'
)

export const monolithShapeListPart = createRoutePart(
    'shape',
    StoneShapeCode,
    'all-shapes'
)

export const monolithShapePart = createRoutePart(
    'shape',
    StoneShapeCode,
    undefined
)

export const colourListPart = createRoutePart(
    'color',
    GemsApi_StoneColour,
    'all-colours'
)

const gemsApiGemstoneColors = Object.values(GemsApi_StoneColour).filter((v) =>
    v.startsWith('GEMSTONE_')
)
export const gemsApiGemstoneColourPart = createRoutePart(
    'color',
    gemsApiGemstoneColors,
    'all-colours',
    (v) => lowercase(v.replace('GEMSTONE_', '').replaceAll('_', '-')),
    (v) =>
        `GEMSTONE_${uppercase(v).replaceAll(
            '-',
            '_'
        )}` as (typeof gemsApiGemstoneColors)[number]
)

const fancyDiamondsColors = Object.values(GemsApi_StoneColour).filter((v) =>
    v.startsWith('FANCY_DIAMOND_')
)
export const fancyDiamondColourParts = createRoutePart(
    'color',
    fancyDiamondsColors,
    'all-colours',
    (v) => lowercase(v.replace('FANCY_DIAMOND_', '').replaceAll('_', '-')),
    (v) =>
        `FANCY_DIAMOND_${uppercase(v).replaceAll(
            '-',
            '_'
        )}` as (typeof fancyDiamondsColors)[number]
)

export const engagementRingStyleListPart = createRoutePart(
    'ringStyle',
    EngagementRingStyleLabelCode,
    'all-ring-styles'
)

export const engagementRingDesignStyleListPart = createRoutePart(
    'designStyle',
    EngagementRingDesignStyleLabelCode,
    'all-design-styles'
)

export const crystalListPart = createRoutePart(
    'crystal',
    GemsApi_StoneCrystal,
    'all-types'
)

export const weddingRingStylePart = createRoutePart(
    'ringStyle',
    WeddingRingStyleLabelCode,
    'all-ring-styles'
)

export const WeddingRingStyleLabelCodeLegacy = ['pave_wr'] as const
export type WeddingRingStyleLabelCodeLegacy =
    (typeof WeddingRingStyleLabelCodeLegacy)[number]

export const weddingRingStylePartLegacy = createRoutePart(
    'ringStyle',
    WeddingRingStyleLabelCodeLegacy,
    undefined
)

const metalSlugMapper = createMapper({
    EIGHTEEN_KARAT_WHITE_GOLD: '18k-white-gold',
    EIGHTEEN_KARAT_YELLOW_GOLD: '18k-yellow-gold',
    EIGHTEEN_KARAT_ROSE_GOLD: '18k-rose-gold',
    FOURTEEN_KARAT_ROSE_GOLD: '14k-rose-gold',
    FOURTEEN_KARAT_WHITE_GOLD: '14k-white-gold',
    FOURTEEN_KARAT_YELLOW_GOLD: '14k-yellow-gold',
    NINE_KARAT_ROSE_GOLD: '9k-rose-gold',
    NINE_KARAT_WHITE_GOLD: '9k-white-gold',
    NINE_KARAT_YELLOW_GOLD: '9k-yellow-gold',
    PLATINUM: 'platinum',
    SILVER: 'silver',
} as const satisfies Record<MetalCode, string>)

const { fromSlug: fromSlugMetal, toSlug: toSlugMetal } = metalSlugMapper

export const metalSlugsListPart = createRoutePart(
    'metal',
    MetalCode,
    'all-metals',
    toSlugMetal,
    fromSlugMetal
)

export const metalSlugsPart = createRoutePart(
    'metal',
    MetalCode,
    undefined,
    toSlugMetal,
    fromSlugMetal
)

const metalCompoundSlugMapper = createCompoundMapper({
    EIGHTEEN_KARAT_WHITE_GOLD: {
        param: 'white-gold',
        hash: '18k',
    },
    EIGHTEEN_KARAT_YELLOW_GOLD: {
        param: 'yellow-gold',
        hash: '18k',
    },
    EIGHTEEN_KARAT_ROSE_GOLD: {
        param: 'rose-gold',
        hash: '18k',
    },
    FOURTEEN_KARAT_ROSE_GOLD: {
        param: 'rose-gold',
        hash: '14k',
    },
    FOURTEEN_KARAT_WHITE_GOLD: {
        param: 'white-gold',
        hash: '14k',
    },
    FOURTEEN_KARAT_YELLOW_GOLD: {
        param: 'yellow-gold',
        hash: '14k',
    },
    NINE_KARAT_ROSE_GOLD: {
        param: 'rose-gold',
        hash: '9k',
    },
    NINE_KARAT_WHITE_GOLD: {
        param: 'white-gold',
        hash: '9k',
    },
    NINE_KARAT_YELLOW_GOLD: {
        param: 'yellow-gold',
        hash: '9k',
    },
    PLATINUM: {
        param: 'platinum',
        hash: undefined,
    },
    SILVER: {
        param: 'silver',
        hash: undefined,
    },
} as const satisfies Record<
    MetalCode,
    { param: string; hash: string | undefined }
>)

export const metalCompoundSlugsListPart = createCompoundRoutePart(
    {
        param: 'metal',
        hash: 'k',
    },
    MetalCode,
    'all-metals',
    metalCompoundSlugMapper.toSlug,
    metalCompoundSlugMapper.fromSlug
)

export const weddingRingCategoriesPart = createRoutePart(
    'category',
    WeddingRingCategoryLabelCode,
    'all-categories'
)

const labGrownColors = Object.values(StoneColorCode).filter((v) =>
    v.startsWith('DIAMOND_')
)
export const labGrownColorPart = createRoutePart(
    'color',
    labGrownColors,
    undefined,
    (v: (typeof labGrownColors)[number]) =>
        lowercase(v.replace('DIAMOND_', '').replaceAll('_', '-')),
    (v) =>
        `DIAMOND_${uppercase(v).replaceAll(
            '-',
            '_'
        )}` as (typeof labGrownColors)[number]
)

const labGrownClarities = Object.values(StoneClarityCode).filter((v) =>
    v.startsWith('DIAMOND_')
)
export const labGrownClarityPart = createRoutePart(
    'clarity',
    labGrownClarities,
    undefined,
    (v) => lowercase(v.replace('DIAMOND_', '').replaceAll('_', '-')),
    (v) =>
        `DIAMOND_${uppercase(v).replaceAll(
            '-',
            '_'
        )}` as (typeof labGrownClarities)[number]
)

const gemstoneColors = Object.values(StoneColorCode).filter((v) =>
    v.startsWith('GEMSTONE_')
)
export const gemstoneColorPart = createRoutePart(
    'color',
    gemstoneColors,
    undefined,
    (v) => lowercase(v.replace('GEMSTONE_', '').replaceAll('_', '-')),
    (v) =>
        `GEMSTONE_${uppercase(v).replaceAll(
            '-',
            '_'
        )}` as (typeof gemstoneColors)[number]
)

const gemstoneCrystals = Object.values(StoneCrystalCode)
export const gemstoneCrystalPart = createRoutePart(
    'crystal',
    gemstoneCrystals,
    undefined
)

const gemstoneColorGrades = Object.values(StoneGradeCode).filter((v) =>
    v.startsWith('GEMSTONE_')
)
export const gemstoneColorGradePart = createRoutePart(
    'colorGrade',
    gemstoneColorGrades,
    undefined,
    (v) => lowercase(v.replace('GEMSTONE_', '').replaceAll('_', '-')),
    (v) =>
        `GEMSTONE_${uppercase(v).replaceAll(
            '-',
            '_'
        )}` as (typeof gemstoneColorGrades)[number]
)

const gemstoneClarities = Object.values(StoneClarityCode).filter((v) =>
    v.startsWith('GEMSTONE_')
)

export const JewelleryProductType = {
    Necklace: 'NECKLACE',
    Earrings: 'EARRINGS',
    Bracelet: 'BRACELET',
    Ring: 'RING',
} as const

export type JewelleryProductType =
    (typeof JewelleryProductType)[keyof typeof JewelleryProductType]

export const jewelleryProductTypesListPart = createRoutePart(
    'productType',
    JewelleryProductType,
    'all-categories'
)

export const jewelleryProductTypesPart = createRoutePart(
    'productType',
    JewelleryProductType,
    undefined
)

export type JewelleryProductStyle =
    | JewelleryBraceletProductStyle
    | JewelleryNecklaceProductStyle
    | JewelleryEarringsProductStyle
    | JewelleryRingProductStyle

export const jewelleryProductStylesPart = createRoutePart(
    'productStyle',
    {
        ...JewelleryBraceletProductStyle,
        ...JewelleryNecklaceProductStyle,
        ...JewelleryEarringsProductStyle,
        ...JewelleryRingProductStyle,
    },
    'all-product-styles'
)

export const jewelleryDesignStylesPart = createRoutePart(
    'designStyle',
    JewelleryDesignStyle,
    'all-design-styles'
)

function createSlugMap<
    const T extends Record<string, string>,
    TR extends {
        [key in keyof T as T[key]]: Lowercase<ReplaceChar<T[key], '_', '-'>>
    },
>(m: T): TR {
    return values(m).reduce(
        (acc, value) => ({
            ...acc,
            [value]: lowercase(value.replace('_', '-')),
        }),
        {} as TR
    )
}

const jewelleryGemstoneSlugMap = createSlugMap(JewelleryGemstoneCode)
const jewelleryGemstones = Object.values(JewelleryGemstoneCode)

const jewelleryGemstoneSlugMapLegacy = {
    ALEXANDRITE_JUN: 'jun_alexandrite',
    AMETHYST_FEB: 'feb_amethyst',
    AQUAMARINE_MAR: 'mar_aquamarine',
    DIAMOND_APR: 'apr_diamond',
    EMERALD_MAY: 'may_emerald',
    GARNET_JAN: 'jan_garnet',
    PEARL: 'pearl',
    PERIDOT_AUG: 'aug_peridot',
    RUBY_JUL: 'jul_ruby',
    SAPPHIRE_SEP: 'sep_sapphire',
    TANZANITE_DEC: 'dec_tanzanite',
    TOPAZ_NOV: 'nov_topaz',
    TOURMALINE_OCT: 'oct_tourmaline',
} as const satisfies Record<JewelleryGemstoneCode, string>

const { fromSlug: fromSlugJewelleryGemstoneLegacy } = createMapper(
    jewelleryGemstoneSlugMapLegacy
)

export const {
    fromSlug: fromSlugJewelleryGemstone,
    toSlug: toSlugJewelleryGemstone,
} = createMapper(jewelleryGemstoneSlugMap)

export const jewelleryGemstonePart = createRoutePart(
    'gemstone',
    JewelleryGemstoneCode,
    'all-gemstones',
    toSlugJewelleryGemstone,
    fromSlugJewelleryGemstone
)

export const gemstoneClarityPart = createRoutePart(
    'clarity',
    gemstoneClarities,
    undefined,
    (v) => lowercase(v.replace('GEMSTONE_', '').replaceAll('_', '-')),
    (v) =>
        `GEMSTONE_${uppercase(v).replaceAll(
            '-',
            '_'
        )}` as (typeof gemstoneClarities)[number]
)

function extractMagicLink(
    route: RouteLocationNormalized
): MagicLinkRouteParams | undefined {
    const email = getString(route.query['user'])
    const expires = getNumber(route.query['expires'])
    const hash = getString(route.query['hash'])

    return email && isValidEmail(email) && expires && hash
        ? {
              email,
              expires,
              hash,
          }
        : undefined
}

// function createRoutes() {
export const regions: string = RegionSlugs.join('|')

const Booking = () => import('@/views/Booking.vue')
const DiamondSearch = () => import('@/views/stone/search/sku/DiamondSearch.vue')
const DtlReport = () => import('@/views/stone/DtlReport.vue')
const NonSkuLabGrownDiamondSearch = () =>
    import('@/views/stone/search/nonsku/LabGrownSearch.vue')
const MagicLinkRequest = () => import('@/views/profile/MagicLinkRequest.vue')
const MagicLink = () => import('@/views/profile/MagicLink.vue')
const Placeholder = () => import('@/views/Placeholder.vue')
const Questionnaire = () => import('@/views/Questionnaire.vue')
const FancyDiamondSearch = () =>
    import('@/views/stone/search/sku/FancyDiamondSearch.vue')
const GemstoneSearch = () =>
    import('@/views/stone/search/sku/GemstoneSearch.vue')
const RedirectDiamondSearchStoneByGiaCert = () =>
    import('@/views/stone/search/legacy/sku/RedirectDiamondSearchStoneByGiaCert.vue')
const RedirectDiamondSearchStoneByLabAndNumber = () =>
    import('@/views/stone/search/legacy/sku/RedirectDiamondSearchStoneByLabAndNumber.vue')
const RedirectDiamondSearchStoneByExternalId = () =>
    import('@/views/stone/search/legacy/sku/RedirectDiamondSearchStoneByExternalId.vue')
const RedirectDiamondSearchFilters = () =>
    import('@/views/stone/search/legacy/sku/RedirectDiamondSearchFilters.vue')
const NonSkuLabGrownDiamondSearchRedirect = () =>
    import('@/views/stone/search/legacy/nonsku/LabGrownRedirect.vue')
const NonSkuGemstoneSearchRedirect = () =>
    import('@/views/stone/search/legacy/nonsku/GemstoneRedirect.vue')
const StoneRecommendations = () =>
    import('@/views/product/engagement-ring/stone-recommendations/StoneRecommendations.vue')
const EngagementRing = () =>
    import('@/views/product/engagement-ring/EngagementRing.vue')
const EngagementRings = () =>
    import('@/views/product/engagement-ring/EngagementRings.vue')
const WeddingRings = () =>
    import('@/views/product/wedding-ring/WeddingRings.vue')
const ReadyToGoRings = () =>
    import('@/views/product/ready-to-go-ring/ReadyToGoRings.vue')
const ReadyToGoRing = () =>
    import('@/views/product/ready-to-go-ring/ReadyToGoRing.vue')
const Jewelleries = () => import('@/views/product/jewellery/Jewelleries.vue')
const JewelleriesRedirectProductType = () =>
    import('@/views/product/jewellery/JewelleriesRedirectProductType.vue')
const Jewellery = () => import('@/views/product/jewellery/Jewellery.vue')
const Redirect = () => import('@/views/Redirect.vue')
const WeddingRingRedirect = () =>
    import('@/views/product/wedding-ring/WeddingRingRedirect.vue')
const Page = () => import('@/views/Page.vue')

const PageProps = (
    route: RouteLocationNormalized
): ExtractComponentProps<typeof Page> => {
    const slug = route.params['slug'] as string[] | string
    if (typeof slug === 'string') {
        return {
            slug: ['/'],
        }
    }
    return {
        slug,
    }
}
const MyOrders = () => import('@/views/profile/my-orders/MyOrders.vue')

const getWeddingRingsListProps = (
    route: RouteLocationNormalized
): ExtractComponentProps<typeof WeddingRings> => {
    const hash = getFragmentFromHash(route.hash)
    return {
        slug: ['/wedding-rings'],
        category: weddingRingCategoriesPart.extractor(route),
        metal: metalSlugsListPart.extractor(route),
        ringStyle: weddingRingStylePart.extractor(route),
        page: Number(route.params['page']) || 1,
        priceMin: getNumber(hash['price_min']),
        priceMax: getNumber(hash['price_max']),
        sortBy: getFromHash(catalogueSortOrder, hash['sort'])[0],
    }
}

const getEngagementRingsListProps = (
    route: RouteLocationNormalized
): ExtractComponentProps<typeof EngagementRings> => {
    const hash = getFragmentFromHash(route.hash)

    return {
        slug: ['/engagement-rings'],
        metal: metalSlugsListPart.extractor(route),
        ringStyle: engagementRingStyleListPart.extractor(route),
        shape: monolithShapeListPart.extractor(route),
        designStyle: engagementRingDesignStyleListPart.extractor(route),
        page: Number(route.params['page']) || 1,
        priceMin: getNumber(hash['price_min']),
        priceMax: getNumber(hash['price_max']),
        sortBy: getFromHash(catalogueSortOrder, hash['sort'])[0],
        designFeatures: getFromHash(
            engagementRingDesignFeatures,
            hash['design_features']
        ),
        recommendationsLayout: route.query['recommendations'] === 'yes',
    }
}

const rootRouteChildrenPartOne = [
    {
        // USELESS, NEVER MATCHED: https://paths.esm.dev/?p=AAMsIPQgwAUKTnNgWALAHgB2K6CcgUAJAD-gUIrA4BgyYQ3girY2YHqAC5A1oAE-ElMAAA..&t=/#
        name: ROUTE_HOME,
        path: '',
        component: () => import('@/views/Page.vue'),
        props() {
            return {
                slug: ['/'],
            }
        },
    },
    {
        name: ROUTE_BOOKING,
        path: 'booking/:step?',
        component: Booking,
        props: (
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof Booking> => {
            const step: BookingStep | undefined = route.params['step'] as
                | BookingStep
                | undefined

            return { step }
        },
    },
    {
        name: ROUTE_MAGIC_LINK_REQUEST,
        path: 'magic-link-request',
        component: MagicLinkRequest,
    },
    {
        name: ROUTE_MAGIC_LINK,
        path: 'magic-link',
        component: MagicLink,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof MagicLink> {
            return {
                magicLink: extractMagicLink(route),
                redirectUrl: getString(route.query['redirectUrl']),
            }
        },
    },
    {
        name: ROUTE_FORGOTTEN_PASSWORD,
        path: 'forgotten-password',
        component: Placeholder,
    },
    {
        // https://taylorandhart.com/diamond-search/lab/GIA-6485927157
        name: 'diamond-search-by-gia-2',
        path: 'diamond-search/lab/GIA-:number(\\d+)',
        component: RedirectDiamondSearchStoneByLabAndNumber,
        props: (
            route: RouteLocationNormalized
        ): ExtractComponentProps<
            typeof RedirectDiamondSearchStoneByLabAndNumber
        > => {
            return {
                number: String(route.params['number']),
            }
        },
    },
    {
        // https://taylorandhart.com/diamond-search/nv-353fca72-56f9-4f5b-a1c0-648e59c3c8b7-GIA-2225072405?shape=round&price=800-1925590&carat=0.31-16.82&canadamark=1&photo=1&provenance=1&cut=EX%20VG&colour=D%20E%20F%20G&clarity=FL%20IF%20VVS1%20VVS2&polish=EX%20VG&symmetry=EX%20VG&fluorescence=none&depth_percent=47.4-81.38&table_percent=50.98-81.4&ratio=0.89-2.36&length=3.19-22.58&width=3.24-17.59&depth=2.09-9.93&view=grid&mode=all
        name: 'diamond-search-by-gia',
        path: 'diamond-search/:feedIdentifier([a-zA-Z0-9]+)-:externalId([a-zA-Z0-9-]+)-GIA-:giaNumber(\\d+)',
        component: RedirectDiamondSearchStoneByGiaCert,
        props: (
            route: RouteLocationNormalized
        ): ExtractComponentProps<
            typeof RedirectDiamondSearchStoneByGiaCert
        > => {
            return {
                feedIdentifier: String(route.params['feedIdentifier']),
                externalId: String(route.params['externalId']),
                giaNumber: String(route.params['giaNumber']),
            }
        },
    },
    {
        // https://taylorandhart.com/diamond-search/nv-c65eac0e-b3c5-4334-98bc-d4638022d184?collection=overall&shape=round&price=230-965245&carat=0.96-15.28&mode=featured
        name: 'diamond-search-by-external-id',
        path: 'diamond-search/:feedIdentifier([a-zA-Z0-9]+)-:externalId([a-zA-Z0-9-]+)',
        component: RedirectDiamondSearchStoneByExternalId,
        props: (
            route: RouteLocationNormalized
        ): ExtractComponentProps<
            typeof RedirectDiamondSearchStoneByExternalId
        > => {
            return {
                feedIdentifier: String(route.params['feedIdentifier']),
                externalId: String(route.params['externalId']),
                giaNumber: String(route.params['giaNumber']),
            }
        },
    },
    {
        // https://taylorandhart.com/eu/diamond-search?collection=overall&shape=round&price=210-1335325&carat=0.2-15.28&mode=featured
        name: 'diamond-search-filters',
        path: 'diamond-search',
        component: RedirectDiamondSearchFilters,
    },
    {
        // https://taylorandhart.com/us/lab-grown-diamond-search?shape=round&price=2921-69370&carat=0.72-7.76&colour=D%20F
        name: 'lab-grown-diamond-search-filters',
        path: 'lab-grown-diamond-search/:stoneId(\\d+)?',
        component: NonSkuLabGrownDiamondSearchRedirect,
        props: (
            route: RouteLocationNormalized
        ): ExtractComponentProps<
            typeof NonSkuLabGrownDiamondSearchRedirect
        > => {
            return {
                stoneId: getNumberOrUndefined(route.params['stoneId']),
            }
        },
    },
    {
        name: ROUTE_NATURAL_DIAMOND_SEARCH,
        path: `diamonds/natural/${shapeListPart.regex}/:page(\\d+)?`, // IF YOU CHANGE THIS - DO IT ALSO IN App\Core\Manager\Jewellery\StoneManager::extractSourceIdentifierFromString, НАМАЛЯМЕ КОМПЛЕКСИТИТО
        component: DiamondSearch,
        props: (
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof DiamondSearch> => {
            return {
                category: GemsApi_StoneCategory.Diamond,
                shape: shapeListPart.extractor(route),
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_LAB_GROWN_DIAMOND_SEARCH,
        path: `diamonds/lab-grown-sku/${shapeListPart.regex}/:page(\\d+)?`,
        component: DiamondSearch,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof DiamondSearch> {
            return {
                category: GemsApi_StoneCategory.LabGrownDiamond,
                shape: shapeListPart.extractor(route),
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_NON_SKU_LAB_GROWN_DIAMOND_SEARCH,
        path: `diamonds/lab-grown`,
        component: NonSkuLabGrownDiamondSearch,
    },
    {
        // https://taylorandhart.com/gemstone-search/123?shape=round&price=946-7040&carat=0.44-3.08&gemstone_type=gemstone_green__sapphire%20gemstone_blue__sapphire
        name: 'gemstone-search-filters',
        path: 'gemstone-search/:stoneId(\\d+)?',
        component: NonSkuGemstoneSearchRedirect,
        props: (
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof NonSkuGemstoneSearchRedirect> => {
            return {
                stoneId: getNumberOrUndefined(route.params['stoneId']),
            }
        },
    },
    {
        name: ROUTE_GEMSTONE_SEARCH,
        path: `gemstones/natural/:page(\\d+)?`,
        component: GemstoneSearch,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof GemstoneSearch> {
            return {
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_NATURAL_FANCY_DIAMOND_SEARCH,
        path: `coloured-diamonds/natural/${fancyDiamondColourParts.regex}/${shapeListPart.regex}/:page(\\d+)?`,
        component: FancyDiamondSearch,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof FancyDiamondSearch> {
            return {
                category: GemsApi_StoneCategory.FancyDiamond,
                colour: fancyDiamondColourParts.extractor(route),
                shape: shapeListPart.extractor(route),
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_LAB_GROWN_FANCY_DIAMOND_SEARCH,
        path: `coloured-diamonds/lab-grown/${fancyDiamondColourParts.regex}/${shapeListPart.regex}/:page(\\d+)?`,
        component: FancyDiamondSearch,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof FancyDiamondSearch> {
            return {
                category: GemsApi_StoneCategory.LabGrownFancyDiamond,
                colour: fancyDiamondColourParts.extractor(route),
                shape: shapeListPart.extractor(route),
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_DTL_REPORT,
        path: `diamonds/:identifier([A-Z0-9]{7})/dtl`,
        component: DtlReport,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof DtlReport> {
            return {
                identifier: String(route.params['identifier']),
            }
        },
    },
    {
        name: ROUTE_ENGAGEMENT_RING_STONE_RECOMMENDATIONS,
        path: 'engagement-rings/stone-recommendations/:category(natural-diamonds|lab-grown-diamonds|gemstones)',
        component: StoneRecommendations,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof StoneRecommendations> {
            let category: GemsApi_StoneCategory = GemsApi_StoneCategory.Diamond

            switch (route.params['category']) {
                case 'natural-diamonds':
                    category = GemsApi_StoneCategory.Diamond
                    break
                case 'lab-grown-diamonds':
                    category = GemsApi_StoneCategory.LabGrownDiamond
                    break
                case 'gemstones':
                    category = GemsApi_StoneCategory.Gemstone
                    break
            }

            return {
                category,
            }
        },
    },
    {
        name: ROUTE_ENGAGEMENT_RING_LAB_GROWN,
        path: `engagement-rings/:slug(.+)-${metalSlugsPart.regex}-engagement-ring-lab-grown-diamond-${monolithShapePart.regex}-${labGrownColorPart.regex}-${labGrownClarityPart.regex}-:caratWeight(\\d+\\.\\d+|\\d+)ct:suffix(.+)?`,
        component: EngagementRing,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof EngagementRing> {
            return getEngagementRingProps(route)
        },
    },
    {
        name: ROUTE_ENGAGEMENT_RING_GEMSTONE,
        path: `engagement-rings/:slug(.+)-${metalSlugsPart.regex}-engagement-ring-natural-gemstone-${gemstoneColorPart.regex}-${gemstoneCrystalPart.regex}-${monolithShapePart.regex}-${gemstoneColorGradePart.regex}-${gemstoneClarityPart.regex}-:caratWeight(\\d+\\.\\d+|\\d+)ct:suffix(.+)?`,
        component: EngagementRing,
        props(route): ExtractComponentProps<typeof EngagementRing> {
            return getEngagementRingProps(route)
        },
    },
    {
        name: ROUTE_ENGAGEMENT_RINGS_HOME,
        path: 'engagement-rings/:page(\\d+)?',
        component: EngagementRings,
        props: getEngagementRingsListProps,
        meta: {
            name: 'engagement-rings',
        },
    },
    {
        name: ROUTE_ENGAGEMENT_RINGS,
        path: `engagement-rings/${metalSlugsListPart.regex}/${engagementRingStyleListPart.regex}/${monolithShapeListPart.regex}/${engagementRingDesignStyleListPart.regex}/:page(\\d+)?`,
        component: EngagementRings,
        props: getEngagementRingsListProps,
        meta: {
            name: 'engagement-rings',
        },
    },
    {
        name: ROUTE_ENGAGEMENT_RING,
        path: `engagement-rings/:slug(.+)-${metalSlugsPart.regex}-engagement-ring-${monolithShapePart.regex}-diamond:suffix(.+)?`,
        component: EngagementRing,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof EngagementRing> {
            return getEngagementRingProps(route)
        },
    },
    {
        name: ROUTE_WEDDING_RINGS_HOME,
        path: 'wedding-rings/:page(\\d+)?',
        component: WeddingRings,
        props: getWeddingRingsListProps,
        meta: {
            name: 'wedding-rings',
        },
    },
    {
        name: ROUTE_WEDDING_RINGS,
        path: `wedding-rings/${weddingRingCategoriesPart.regex}/${metalSlugsListPart.regex}/${weddingRingStylePart.regex}/:page(\\d+)?`,
        component: WeddingRings,
        props: getWeddingRingsListProps,
        meta: {
            name: 'wedding-rings',
        },
    },
    {
        name: ROUTE_WEDDING_RING,
        path: `wedding-rings/:slug(.+)-${metalSlugsPart.regex}-wedding-ring:suffix(.+)?`,
        component: () => import('@/views/product/wedding-ring/WeddingRing.vue'),
        props(route: RouteLocationNormalized): Record<string, any> {
            return {
                ...route.params,
                suffixSlug:
                    typeof route.params['suffix'] === 'string'
                        ? route.params['suffix'].substring(1)
                        : route.params['suffix'],
                metal: metalSlugsPart.extractor(route),
            }
        },
    },
    {
        name: 'wedding-ring-redirects',
        path: `wedding-rings/${weddingRingCategoriesPart.regex}/${metalSlugsListPart.regex}/${weddingRingStylePartLegacy.regex}/:page(\\d+)?`,
        component: WeddingRingRedirect,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof WeddingRingRedirect> {
            const hash = getFragmentFromHash(route.hash)
            return {
                category: weddingRingCategoriesPart.extractor(route),
                metal: metalSlugsListPart.extractor(route),
                ringStyle: weddingRingStylePartLegacy.extractor(route),
                page: Number(route.params['page']) || 1,
                priceMin: getNumber(hash['price_min']),
                priceMax: getNumber(hash['price_max']),
            }
        },
    },
    {
        name: 'wedding-ring-redirects-2',
        path: `wedding-rings/:category(ladies|mens)-wedding-rings/`,
        component: Redirect,
        props(route): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/wedding-rings/${route.params['category']}`,
            }
        },
    },
    {
        name: 'ring-guidance/the-crafting-process',
        path: 'ring-guidance/the-crafting-process',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/ring-guidance`,
            }
        },
    },
    {
        name: 'ring-guidance/the-crafting-process/how-our-rings-are-created',
        path: 'ring-guidance/the-crafting-process/how-our-rings-are-created',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/ring-guidance`,
            }
        },
    },
    {
        name: 'ring-guidance/engagement-ring-cost/transparency',
        path: 'ring-guidance/engagement-ring-cost/transparency',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/ring-guidance/engagement-ring-cost`,
            }
        },
    },
    {
        name: 'engagement-rings/hidden-halo-engagement-rings',
        path: 'engagement-rings/hidden-halo-engagement-rings',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/engagement-rings/all-metals/all-ring-styles/all-shapes/hidden_halo`,
            }
        },
    },
    {
        name: 'engagement-rings/twisted-engagement-rings',
        path: 'engagement-rings/twisted-engagement-rings',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/engagement-rings/all-metals/all-ring-styles/all-shapes/twisted`,
            }
        },
    },
    {
        name: 'engagement-rings/east-west',
        path: 'engagement-rings/east-west',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/engagement-rings/all-metals/all-ring-styles/all-shapes/east_west`,
            }
        },
    },
    {
        name: 'engagement-rings/bezel-setting',
        path: 'engagement-rings/bezel-setting',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/engagement-rings/all-metals/all-ring-styles/all-shapes/bezel`,
            }
        },
    },
    {
        name: 'engagement-rings/split-shank',
        path: 'engagement-rings/split-shank',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/engagement-rings/all-metals/all-ring-styles/all-shapes/split_shank`,
            }
        },
    },
    {
        name: 'engagement-rings/cathedral-setting',
        path: 'engagement-rings/cathedral-setting',
        component: Redirect,
        props(): ExtractComponentProps<typeof Redirect> {
            return {
                location: `/engagement-rings/all-metals/all-ring-styles/all-shapes/cathedral`,
            }
        },
    },
] as const satisfies RouteRecordRaw[]

const rootRouteChildrenPartTwo = [
    {
        name: ROUTE_READY_TO_GO_RING,
        path: `ready-to-go/${metalSlugsPart.regex}-:slug(.+)`,
        component: ReadyToGoRing,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof ReadyToGoRing> {
            return {
                slug: getString(route.params['slug']) || '',
                metal: metalSlugsPart.extractor(route),
            }
        },
    },
    {
        name: ROUTE_READY_TO_GO_RINGS,
        path: `ready-to-go/${metalSlugsListPart.regex}/:page(\\d+)?`,
        component: ReadyToGoRings,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof ReadyToGoRings> {
            const hash = getFragmentFromHash(route.hash)
            return {
                slug: ['/ready-to-go'],
                metal: metalSlugsListPart.extractor(route),
                page: Number(route.params['page']) || 1,
                priceMin: getNumber(hash['price_min']),
                priceMax: getNumber(hash['price_max']),
                sortBy: getFromHash(catalogueSortOrder, hash['sort'])[0],
            }
        },
    },
    {
        name: 'rtsList',
        path: `rts-list/${metalSlugsListPart.regex}/:page(\\d+)?`,
        component: ReadyToGoRings,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof ReadyToGoRings> {
            const hash = getFragmentFromHash(route.hash)
            return {
                slug: ['/ready-to-go'],
                metal: metalSlugsListPart.extractor(route),
                page: Number(route.params['page']) || 1,
                priceMin: getNumber(hash['price_min']),
                priceMax: getNumber(hash['price_max']),
                readyToShip: true,
                sortBy: getFromHash(catalogueSortOrder, hash['sort'])[0],
            }
        },
    },
    {
        name: 'jewelleryOldProductTypeRedirect',
        path: `jewellery/all-product-types/${jewelleryProductStylesPart.regex}/${metalSlugsListPart.regex}/${jewelleryDesignStylesPart.regex}/:page(\\d+)?`,
        component: JewelleriesRedirectProductType,
        props(
            route: RouteLocationNormalized
        ): JewelleriesRedirectProducTypeProps {
            const metal = metalSlugsListPart.extractor(route)
            const hash = getFragmentFromHash(route.hash)

            return {
                productStyle: jewelleryProductStylesPart.extractor(route),
                metal: metal && [metal],
                designStyle: jewelleryDesignStylesPart.extractor(route),
                page: Number(route.params['page']) || 1,
                priceMin: getNumber(hash['price_min']),
                priceMax: getNumber(hash['price_max']),
            }
        },
    },
    {
        name: ROUTE_JEWELLERIES,
        path: `jewellery/${jewelleryProductTypesListPart.regex}/${jewelleryProductStylesPart.regex}/${jewelleryGemstonePart.regex}/${jewelleryDesignStylesPart.regex}/${metalCompoundSlugsListPart.regex}/:page(\\d+)?`,
        component: Jewelleries,
        props(route: RouteLocationNormalized): JewelleriesProps {
            const hash = getFragmentFromHash(route.hash)

            return {
                productType: jewelleryProductTypesListPart.extractor(route),
                productStyle: jewelleryProductStylesPart.extractor(route),
                gemstone: jewelleryGemstonePart.extractor(route),
                designStyle: jewelleryDesignStylesPart.extractor(route),
                metal: metalCompoundSlugsListPart.extractor(route),
                page: Number(route.params['page']) || 1,
                priceMin: getNumber(hash['price_min']),
                priceMax: getNumber(hash['price_max']),
                sortBy: getFromHash(catalogueSortOrder, hash['sort'])[0],
                legacyGemstones: getFromHash(
                    jewelleryGemstones,
                    hash['design_features'],
                    fromSlugJewelleryGemstoneLegacy
                ),
            }
        },
    },
    {
        name: ROUTE_JEWELLERY,
        path: `jewellery/:slug(.+)-${metalSlugsPart.regex}-${jewelleryProductTypesPart.regex}:suffix(.+)?`,
        component: Jewellery,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof Jewellery> {
            return {
                productType: jewelleryProductTypesPart.extractor(route),
                metal: metalSlugsPart.extractor(route),
                slug: getString(route.params['slug']) || '',
                suffixSlug:
                    typeof route.params['suffix'] === 'string'
                        ? route.params['suffix'].substring(1)
                        : '',
            }
        },
    },
    {
        name: 'jewelleryOldDesignFeaturesRedirect',
        path: `jewellery/${jewelleryProductTypesListPart.regex}/${jewelleryProductStylesPart.regex}/${metalCompoundSlugsListPart.regex}/${jewelleryDesignStylesPart.regex}/:page(\\d+)?`,
        component: JewelleriesRedirectProductType,
        props(
            route: RouteLocationNormalized
        ): JewelleriesRedirectProducTypeProps {
            const hash = getFragmentFromHash(route.hash)

            return {
                productType: jewelleryProductTypesListPart.extractor(route),
                productStyle: jewelleryProductStylesPart.extractor(route),
                metal: metalCompoundSlugsListPart.extractor(route),
                designStyle: jewelleryDesignStylesPart.extractor(route),
                page: Number(route.params['page']) || 1,
                priceMin: getNumber(hash['price_min']),
                priceMax: getNumber(hash['price_max']),
                legacyGemstones: getFromHash(
                    jewelleryGemstones,
                    hash['design_features'],
                    fromSlugJewelleryGemstoneLegacy
                ),
            }
        },
    },
    {
        name: 'jewelleryOldMetalRedirect',
        path: `jewellery/${jewelleryProductTypesListPart.regex}/${jewelleryProductStylesPart.regex}/${metalSlugsListPart.regex}/${jewelleryDesignStylesPart.regex}/:page(\\d+)?`,
        component: JewelleriesRedirectProductType,
        props(
            route: RouteLocationNormalized
        ): JewelleriesRedirectProducTypeProps {
            const hash = getFragmentFromHash(route.hash)
            const metal = metalSlugsListPart.extractor(route)

            return {
                productStyle: jewelleryProductStylesPart.extractor(route),
                metal: metal && [metal],
                designStyle: jewelleryDesignStylesPart.extractor(route),
                page: Number(route.params['page']) || 1,
                priceMin: getNumber(hash['price_min']),
                priceMax: getNumber(hash['price_max']),
                legacyGemstones: getFromHash(
                    jewelleryGemstones,
                    hash['design_features'],
                    fromSlugJewelleryGemstoneLegacy
                ),
            }
        },
    },
    {
        name: ROUTE_BLOG,
        path: 'blog/:page(\\d+)?',
        component: () => import('@/views/blog/Blog.vue'),
        props(route: RouteLocationNormalized) {
            return {
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_BLOG_CATEGORY,
        path: 'blog/category/:slug/:page(\\d+)?',
        component: () => import('@/views/blog/BlogCategory.vue'),
        props(route: RouteLocationNormalized): Record<string, any> {
            return {
                slug: route.params['slug'],
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_BLOG_TAG,
        path: 'blog/tag/:slug/:page(\\d+)?',
        component: () => import('@/views/blog/BlogTag.vue'),
        props(route: RouteLocationNormalized): Record<string, any> {
            return {
                slug: route.params['slug'],
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_BLOG_AUTHOR,
        path: 'blog/author/:author/:page(\\d+)?',
        component: () => import('@/views/blog/BlogAuthor.vue'),
        props(route: RouteLocationNormalized): Record<string, any> {
            return {
                author: route.params['author'],
                page: Number(route.params['page']) || 1,
            }
        },
    },
    {
        name: ROUTE_BLOG_POST,
        path: 'blog/:slug',
        component: () => import('@/views/blog/BlogPost.vue'),
        props(route: RouteLocationNormalized): Record<string, any> {
            return {
                slug: route.params['slug'],
            }
        },
    },
    {
        name: ROUTE_PAGE,
        path: ':slug([^/]*)*',
        component: Page,
        props: PageProps,
    },
    {
        name: ROUTE_QUESTIONNAIRE,
        path: 'questionnaire/:id/:step?',
        component: Questionnaire,
        props(
            route: RouteLocationNormalized
        ): ExtractComponentProps<typeof Questionnaire> {
            return {
                id: route.params['id'] as string, // Guaranteed by PATH ABOVE
                magicLink: extractMagicLink(route),
            }
        },
    },
    {
        name: '__internal_profile',
        path: 'profile',
        component: () => import('@/views/profile/Profile.vue'),
        children: [
            {
                name: ROUTE_PROFILE_DESIGN_STUDIO,
                path: 'designstudio',
                component: () => import('@/views/profile/DesignStudio.vue'),
            },
            {
                name: ROUTE_PROFILE_PRODUCTS,
                path: 'products',
                component: () => import('@/views/profile/DesignStudio.vue'),
            },
            {
                name: ROUTE_PROFILE_PRODUCT,
                path: 'product/:id?',
                component: () => import('@/views/profile/DesignStudio.vue'),
            },
            {
                name: ROUTE_PROFILE_MY_ORDERS,
                path: 'my-orders',
                component: MyOrders,
                props: (
                    route: RouteLocationNormalized
                ): ExtractComponentProps<typeof MyOrders> => {
                    const orderId = route.query['id']

                    return {
                        orderId: orderId && String(orderId),
                    }
                },
            },
        ],
    },
    {
        name: ROUTE_PROFILE_MAKE_PAYMENT,
        path: 'makePayment/:orderId',
        component: () => import('@/views/profile/my-orders/MyOrders.vue'),
    },
    {
        name: ROUTE_SIGN_IN,
        path: 'sign-in',
        component: () => import('@/views/profile/SignIn.vue'),
    },
    {
        name: ROUTE_CART,
        path: 'cart/view',
        component: () => import('@/views/checkout/Cart.vue'),
    },
    {
        name: ROUTE_CHECKOUT,
        path: 'checkout/:orderId(\\d+)',
        component: () => import('@/views/checkout/Checkout.vue'),
        props: (route) => {
            return {
                orderId: Number(route.params['orderId']),
            }
        },
        // Transition for siblings is handled in nested router-view
        // Using the same key prevents root transition from being triggered
        children: [
            {
                name: '__checkout_redirect',
                path: '',
                redirect: (to) => {
                    return {
                        name: ROUTE_CHECKOUT_DELIVERY,
                        params: {
                            orderId: to.params['orderId'],
                        },
                    }
                },
                meta: { name: ROUTE_CHECKOUT },
            },
            {
                name: ROUTE_CHECKOUT_DELIVERY,
                path: 'shipping',
                components: {
                    default: () =>
                        import('@/views/checkout/step/delivery/DeliveryForm.vue'),
                    sidebar: () =>
                        import('@/views/checkout/step/delivery/DeliveryActions.vue'),
                },
                meta: { name: ROUTE_CHECKOUT },
            },
            {
                name: ROUTE_CHECKOUT_PAYMENT,
                path: 'payment',
                // component: () => import('@/views/checkout/step/StepPayment.vue'),
                components: {
                    default: () =>
                        import('@/views/checkout/step/payment/PaymentForm.vue'),
                    sidebar: () =>
                        import('@/views/checkout/step/payment/PaymentActions.vue'),
                },
                meta: { name: ROUTE_CHECKOUT },
            },
            {
                name: ROUTE_CHECKOUT_REVIEW,
                path: 'review',
                component: () =>
                    import('@/views/checkout/step/review/ReviewForm.vue'),
                meta: { name: ROUTE_CHECKOUT },
            },
        ],
    },
    {
        name: ROUTE_CHECKOUT_COMPLETE,
        path: 'thank-you',
        component: () => import('@/views/checkout/ThankYou.vue'),
    },
    {
        name: ROUTE_CHECKOUT_PAYMENT_CHECKOUTCOM_RETURN,
        path: 'checkoutcom-return/:orderId',
        component: () =>
            import('@/views/checkout/return/StepPaymentCheckoutcomReturn.vue'),
    },
    {
        name: ROUTE_CHECKOUT_PAYMENT_AFFIRM_RETURN,
        path: 'affirm-return/:orderId',
        component: () =>
            import('@/views/checkout/return/StepPaymentAffirmReturn.vue'),
    },
    {
        name: ROUTE_IMPERSONATE,
        path: 'impersonate/:orderId',
        component: () => import('@/views/Impersonate.vue'),
        props: (route) => ({
            orderId: route.params['orderId'],
        }),
    },
] as const satisfies RouteRecordRaw[]

export type RoutesPartOne = typeof rootRouteChildrenPartOne
export type RoutesPartTwo = typeof rootRouteChildrenPartTwo

const rootRoute = {
    path: `/:region(${regions})?`,
    name: '__translator_internal',
    component: Region,
    props(
        route: RouteLocationNormalized
    ): ExtractComponentProps<typeof Region> {
        const region = route.params['region'] || ''

        if (typeof region !== 'string') {
            return {
                region: defaultRegion,
            }
        }

        return { region }
    },
    children: [
        ...rootRouteChildrenPartOne,
        ...rootRouteChildrenPartTwo,
    ] satisfies RouteRecordRaw[],
} satisfies RouteRecordRaw

const initialScroll = { left: 0, top: 0, behavior: 'smooth' } as const

export function createRouter(
    history: RouterHistory,
    isMobile: ComputedRef<boolean>
): Router {
    const { isLazyLoadInProgress, resolveScrollPosition } = storeToRefs(
        // eslint-disable-next-line vue-composable/composable-placement
        useScrollPositionResolverStore()
    )

    return vueCreateRouter({
        history,
        routes,
        scrollBehavior: async (to, from, savedPosition) => {
            if (to.hash !== from.hash) {
                // makes sure it scrolls to top when navigating to filtered
                // stone recommendations
                if (to.hash.includes('filters') && to.name !== from.name) {
                    return initialScroll
                }

                // called in browser context only
                // eslint-disable-next-line no-restricted-globals
                const header = document.querySelector('.app-header')

                const top =
                    !isMobile.value && header
                        ? parseFloat(getComputedStyle(header).height)
                        : 0

                return {
                    el: to.hash || undefined,
                    top,
                    behavior: 'smooth',
                }
            }

            if (savedPosition) {
                return savedPosition
            } else if (to.name === from.name) {
                // Reset scroll when navigating between
                // CMS pages and blog posts
                if (
                    (to.name === ROUTE_PAGE &&
                        from.name === ROUTE_PAGE &&
                        typeof to.params['slug'] === 'object' &&
                        typeof from.params['slug'] === 'object' &&
                        difference(to.params['slug'], from.params['slug'])
                            .length) ||
                    (to.name === ROUTE_BLOG_POST &&
                        from.name === ROUTE_BLOG_POST &&
                        typeof to.params['slug'] === 'string' &&
                        typeof from.params['slug'] === 'string' &&
                        to.params['slug'] !== from.params['slug'])
                ) {
                    return initialScroll
                }
            } else {
                if (!isLazyLoadInProgress.value) {
                    return initialScroll
                } else {
                    // Postpone scroll until chunk lazy load is complete
                    return new Promise((resolve) => {
                        resolveScrollPosition.value = () =>
                            resolve(initialScroll)
                    })
                }
            }
        },
        parseQuery(query: string): LocationQuery {
            return qs.parse(query, parseOptions) as LocationQuery
        },
        stringifyQuery(query: LocationQueryRaw | undefined = {}): string {
            return qs.stringify(query, stringifyOptions)
        },
    })
}

export const routes = [
    {
        ...rootRoute,

        children: [...rootRoute.children, ...(QA ? testRoutes : [])],
    },
] satisfies RouteRecordRaw[]
